import { useQuery } from '@tanstack/react-query';
import {
  configApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { StaticResourceVersion } from '@internal/backstage-plugin-bidone-backend';
import { handleReponse } from '../helper';
import { UseQueryCustomResult } from '../types';
import { customComponentMap } from '../components/VersionChecker/types';

const fetchStaticContent = async (
  product: string,
  contentType: string,
  backendUrl: string,
  accessToken?: string,
  env?: string,
): Promise<Record<string, Record<string, StaticResourceVersion>>> => {
  if (!env) {
    return {};
  }

  const response = await fetch(
    `${backendUrl}/api/bidone/static-content/latest-version`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      body: JSON.stringify({
        product,
        contentType,
        env,
      }),
    },
  );

  return handleReponse(response);
};

const useStaticContentLatestVersion = (
  product: 'bdirect-mfe',
  contentType: 'js' | 'css',
  env: string,
  deploymentGroup: string,
): UseQueryCustomResult<
  Record<string, Record<string, StaticResourceVersion>>
> => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const staticContentKey = `${product}-${contentType}`.toLowerCase();

  return useQuery({
    retry: false,
    queryKey: [
      'useStaticContent',
      'useStaticContentLatestVersion',
      staticContentKey,
      env,
      deploymentGroup,
    ],
    queryFn: async () => {
      // This deployment group has no static content to load
      if (!customComponentMap[deploymentGroup]?.[staticContentKey]) {
        return {};
      }

      const { token } = await identityApi.getCredentials();

      return fetchStaticContent(product, contentType, backendUrl, token, env);
    },
  });
};

export { useStaticContentLatestVersion };
